import { useEffect, useState } from 'react';
import { IOption } from '@vtbhome/juicyfront/types';
import { useMemo } from 'react';
import { useFileOptionsParams } from './types';
import { debounce } from '@vtbhome/juicyfront/utils/helpers';
import { SELECT_INFINITY_PAGINATION_ITEMS_ON_PAGE } from '@shared/ui/select/constants';
import { FileOption } from '@shared/ui/input-file-search/file-option/file-option';

export const useFileSelect = ({
    fileData,
    isLoading,
    isFetching,
    accept,
    value,
    setFilesListParameters,
}: useFileOptionsParams) => {
    const [isShowEmptyLoader, setIsShowEmptyLoader] = useState(isLoading);

    const emptyOptions = useMemo(() => {
        /* Нужен пустой массив без потери ссылки после рендера */
        return [] as IOption[];
    }, []);

    const filteredFileSuggestions = useMemo(() => {
        const suggestions = fileData?.items || [];
        const deletedUploadedIds = value.deletedUploadedFiles.map((file) => file.id);
        const addedUploadedIds = value.addedUploadedFiles.map((file) => file.id);
        const initialRemainedUploadedFiles = value.initialUploadedFiles.filter((file) =>
            deletedUploadedIds.includes(file.id)
        );
        const allUploadedIds = [...addedUploadedIds, initialRemainedUploadedFiles];
        return suggestions.filter((file) => !allUploadedIds.includes(file.id));
    }, [value, fileData?.items]);

    const options: IOption[] = useMemo(() => {
        return (
            filteredFileSuggestions.map((file) => {
                const disabledByAccept = accept ? !accept.includes(file.meta.extension) : false;
                return {
                    label: file.name,
                    value: String(file.id),
                    disabled: disabledByAccept,
                    node: <FileOption file={file} disabled={disabledByAccept} />,
                };
            }) || emptyOptions
        );
    }, [emptyOptions, filteredFileSuggestions]);

    const infinityScrollFilesProps = useMemo(() => {
        return {
            dataLength: fileData?.items.length || 0,
            hasMore: (fileData?.items.length || 0) < (fileData?.total || 0),
        };
    }, [fileData]);

    const handleSearch = useMemo(() => {
        return debounce((query: string, isPagination?: boolean | undefined) => {
            return setFilesListParameters((prev) => {
                setIsShowEmptyLoader(true);
                return {
                    ...prev,
                    skip: isPagination ? (prev.skip || 0) + SELECT_INFINITY_PAGINATION_ITEMS_ON_PAGE : null,
                    search: query,
                };
            });
        }, 300);
    }, [setFilesListParameters]);

    useEffect(() => {
        if (!isFetching) {
            setIsShowEmptyLoader(false);
        }
    }, [isFetching, isShowEmptyLoader]);

    return {
        filesOptions: isShowEmptyLoader ? emptyOptions : options,
        filesEmptyOption: emptyOptions,
        isShowEmptyLoaderFliles: isShowEmptyLoader,
        filteredFileData: filteredFileSuggestions,
        infinityScrollFilesProps,
        onSearchFiles: handleSearch,
    };
};
